import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertCircle,
  Bot,
  Check,
  CheckCircle2,
  Clipboard,
  ExternalLink,
  KeyRound,
  Link2,
  LoaderCircle,
  Plus,
  RefreshCw,
  Save,
  ShieldCheck,
  ShoppingBag,
  Video,
  X,
} from 'lucide-react';
import { useMemo, useState, type FormEvent, type ReactNode } from 'react';
import { errorMessage } from '../../lib/api';
import { appPath } from '../../lib/navigation';
import { queryKeys } from '../../lib/queryClient';
import type {
  GuestAccessConfig,
  RecallConfig,
  WhopConfig,
  WhopProbeProduct,
  ZoomConnectionStatus,
  ZoomSetupAccount,
  ZoomSetupConfig,
} from '../../types';
import { getZoomConnectionStatus } from './eventsApi';
import {
  getGuestAccess,
  getRecallConfig,
  getWhopConfig,
  getZoomSetup,
  probeWhop,
  saveGuestPasscode,
  saveRecallConfig,
  saveWhopConfig,
  saveZoomAccounts,
  type ZoomAccountInput,
} from './setupApi';
import { ModulePage } from './ModulePage';

const MAX_ACCOUNTS = 2;
const recallRegions = [
  ['us-east-1', 'US East'],
  ['us-west-2', 'US West'],
  ['eu-central-1', 'Europe'],
  ['ap-northeast-1', 'Asia'],
] as const;

type Feedback = { tone: 'ok' | 'error'; text: string } | null;

export function SetupManager() {
  const queryClient = useQueryClient();
  const zoom = useQuery({ queryKey: queryKeys.zoomSetup, queryFn: ({ signal }) => getZoomSetup(signal) });
  const connection = useQuery({
    queryKey: queryKeys.zoomStatus,
    queryFn: ({ signal }) => getZoomConnectionStatus(signal),
    staleTime: 30_000,
  });
  const guest = useQuery({ queryKey: queryKeys.guestAccess, queryFn: ({ signal }) => getGuestAccess(signal) });
  const recall = useQuery({ queryKey: queryKeys.recallConfig, queryFn: ({ signal }) => getRecallConfig(signal) });
  const whop = useQuery({ queryKey: queryKeys.whopConfig, queryFn: ({ signal }) => getWhopConfig(signal) });
  const refreshing = zoom.isFetching || connection.isFetching || guest.isFetching || recall.isFetching || whop.isFetching;

  async function refreshAll() {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: queryKeys.zoomSetup }),
      queryClient.invalidateQueries({ queryKey: queryKeys.zoomStatus }),
      queryClient.invalidateQueries({ queryKey: queryKeys.guestAccess }),
      queryClient.invalidateQueries({ queryKey: queryKeys.recallConfig }),
      queryClient.invalidateQueries({ queryKey: queryKeys.whopConfig }),
    ]);
  }

  return (
    <ModulePage
      id="setup-title"
      title="Setup"
      className="setup-manager"
      innerClassName="setup-manager__inner"
      meta={<span className="module-page__note">Credentials stay write-only</span>}
      actions={(
        <button className="icon-button" type="button" title="Refresh setup" aria-label="Refresh setup" disabled={refreshing} onClick={() => void refreshAll()}>
          <RefreshCw className={refreshing ? 'spin' : ''} size={17} />
        </button>
      )}
    >
      <div className="setup-layout">
        <div className="setup-column">
          <SetupGuide />
          <ConnectionPanel
            config={zoom.data}
            configError={zoom.isError ? errorMessage(zoom.error, 'Zoom setup could not be loaded.') : null}
            status={connection.data}
            statusError={connection.isError ? errorMessage(connection.error, 'Connection status is unavailable.') : null}
            checking={connection.isFetching}
            onCheck={() => void connection.refetch()}
          />
          <ZoomAccountsPanel config={zoom.data} loading={zoom.isPending} onSaved={refreshAll} />
        </div>

        <div className="setup-column">
          <GuestAccessPanel config={guest.data} loading={guest.isPending} error={guest.isError ? errorMessage(guest.error, 'Guest access could not be loaded.') : null} />
          <RecallPanel key={recall.data ? `${recall.data.platform}:${recall.data.region}:${recall.data.own}` : 'recall-loading'} config={recall.data} loading={recall.isPending} error={recall.isError ? errorMessage(recall.error, 'Reply bot setup could not be loaded.') : null} />
          <WhopPanel key={whop.data ? `${whop.data.keySet}:${whop.data.companyId}:${whop.data.products.length}` : 'whop-loading'} config={whop.data} loading={whop.isPending} error={whop.isError ? errorMessage(whop.error, 'Whop setup could not be loaded.') : null} />
        </div>
      </div>
    </ModulePage>
  );
}

function SetupGuide() {
  return (
    <SetupPanel icon={<Video size={16} />} title="Zoom app checklist" className="setup-guide">
      <ol>
        <li>Create a <strong>Server-to-Server OAuth</strong> app in the <a href="https://marketplace.zoom.us" target="_blank" rel="noreferrer">Zoom Marketplace <ExternalLink size={12} /></a>.</li>
        <li>Add <code>meeting:read:chat_message:admin</code> and <code>webinar:read:chat_message:admin</code>.</li>
        <li>Add an event subscription using the webhook URL below, then subscribe to chat messages and ended events for meetings and webinars.</li>
        <li>Enter the app credentials and save the account.</li>
      </ol>
    </SetupPanel>
  );
}

interface ConnectionPanelProps {
  config: ZoomSetupConfig | undefined;
  configError: string | null;
  status: ZoomConnectionStatus | undefined;
  statusError: string | null;
  checking: boolean;
  onCheck: () => void;
}

function ConnectionPanel({ config, configError, status, statusError, checking, onCheck }: ConnectionPanelProps) {
  const webhookUrl = config ? `${window.location.origin}${config.webhookPath}` : '';
  return (
    <SetupPanel icon={<Link2 size={16} />} title="Connection status" action={
      <button className="icon-button" type="button" title="Re-check Zoom connection" aria-label="Re-check Zoom connection" disabled={checking} onClick={onCheck}>
        <RefreshCw className={checking ? 'spin' : ''} size={15} />
      </button>
    }>
      {configError ? <InlineState tone="error">{configError}</InlineState> : null}
      {statusError ? <InlineState tone="error">{statusError}</InlineState> : null}
      {!status && !statusError ? <InlineState><LoaderCircle className="spin" size={15} />Checking connection</InlineState> : null}
      {status ? <ZoomStatusRows status={status} /> : null}
      {webhookUrl ? (
        <div className="setup-copy-block">
          <span>Webhook URL</span>
          <div><code>{webhookUrl}</code><CopyButton value={webhookUrl} label="Copy webhook URL" /></div>
        </div>
      ) : null}
    </SetupPanel>
  );
}

function ZoomStatusRows({ status }: { status: ZoomConnectionStatus }) {
  if (status.granted) {
    return <InlineState tone="ok"><CheckCircle2 size={16} />Using {status.grantedFrom || 'your provider'}&apos;s shared Zoom account</InlineState>;
  }
  const configured = status.accounts.filter(account => account.configured);
  if (!configured.length) return <InlineState><AlertCircle size={16} />No Zoom account connected</InlineState>;
  return <div className="setup-status-list">{configured.map(account => (
    <div className={account.ok ? 'ok' : 'error'} key={account.account}>
      {account.ok ? <CheckCircle2 size={16} /> : <AlertCircle size={16} />}
      <span><strong>Zoom account {account.account}</strong><small>{account.ok ? 'Authenticated, credentials valid' : `Not connected: ${account.reason || 'no credentials'}`}</small></span>
    </div>
  ))}</div>;
}

function ZoomAccountsPanel({ config, loading, onSaved }: { config: ZoomSetupConfig | undefined; loading: boolean; onSaved: () => Promise<void> }) {
  const [adding, setAdding] = useState(false);
  const [feedback, setFeedback] = useState<Feedback>(null);
  const configured = useMemo(() => config?.accounts.filter(account => account.accountId) ?? [], [config]);
  const nextIndex = [1, 2].find(index => !configured.some(account => account.index === index)) ?? null;
  const save = useMutation({
    mutationFn: (accounts: ZoomAccountInput[]) => saveZoomAccounts(accounts),
    onSuccess: async () => {
      setFeedback({ tone: 'ok', text: 'Zoom account saved.' });
      setAdding(false);
      await onSaved();
    },
    onError: error => setFeedback({ tone: 'error', text: errorMessage(error, 'Zoom account could not be saved.') }),
  });

  return (
    <SetupPanel icon={<KeyRound size={16} />} title="Zoom accounts">
      {loading ? <InlineState><LoaderCircle className="spin" size={15} />Loading accounts</InlineState> : null}
      {config?.granted ? (
        <InlineState tone="ok"><ShieldCheck size={17} />You use {config.grantedFrom || 'your provider'}&apos;s shared Zoom account. Add meetings under Events; no credentials are needed here.</InlineState>
      ) : null}
      {config && !config.granted && configured.length === 0 && !adding ? (
        <div className="setup-empty"><Video size={20} /><strong>No Zoom account connected</strong><span>Add your account to start receiving chat.</span></div>
      ) : null}
      {!config?.granted ? configured.map(account => (
        <ZoomAccountForm account={account} key={`${account.index}:${account.accountId}:${account.clientId}`} pending={save.isPending} onSave={input => save.mutateAsync([input]).then(() => undefined)} />
      )) : null}
      {adding && nextIndex ? (
        <NewZoomAccountForm index={nextIndex} webhookUrl={`${window.location.origin}${config?.webhookPath || '/webhook'}`} pending={save.isPending} onCancel={() => setAdding(false)} onSave={input => save.mutateAsync([input]).then(() => undefined)} />
      ) : null}
      {!config?.granted && nextIndex && !adding ? (
        <button className="primary-button compact" type="button" onClick={() => { setFeedback(null); setAdding(true); }}><Plus size={15} />{configured.length ? 'Add another account' : 'Add Zoom account'}</button>
      ) : null}
      {configured.length === MAX_ACCOUNTS ? <p className="setup-footnote">Both available Zoom account slots are configured.</p> : null}
      {feedback ? <InlineState tone={feedback.tone}>{feedback.tone === 'ok' ? <Check size={15} /> : <AlertCircle size={15} />}{feedback.text}</InlineState> : null}
      {configured.length ? <p className="setup-footnote">Leave a secret field blank to keep the saved value. Secrets are never shown.</p> : null}
    </SetupPanel>
  );
}

function ZoomAccountForm({ account, pending, onSave }: { account: ZoomSetupAccount; pending: boolean; onSave: (input: ZoomAccountInput) => Promise<void> }) {
  const [clientSecret, setClientSecret] = useState('');
  const [webhookSecret, setWebhookSecret] = useState('');
  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const values = new FormData(event.currentTarget);
    try {
      await onSave({
        index: account.index,
        accountId: String(values.get('accountId') || '').trim(),
        clientId: String(values.get('clientId') || '').trim(),
        clientSecret,
        webhookSecret,
      });
      setClientSecret('');
      setWebhookSecret('');
    } catch { /* Mutation feedback is rendered by the parent panel. */ }
  }
  return (
    <form className="zoom-account-form" onSubmit={event => void submit(event)}>
      <header><strong>Zoom account {account.index}</strong><span>Connected slot</span></header>
      <div className="setup-form-grid">
        <SetupField label={`Account ID, account ${account.index}`} name="accountId" defaultValue={account.accountId} />
        <SetupField label={`Client ID, account ${account.index}`} name="clientId" defaultValue={account.clientId} />
        <SetupField label={`Client Secret, account ${account.index}`} name="clientSecret" type="password" value={clientSecret} onChange={setClientSecret} saved={account.clientSecretSet} />
        <SetupField label={`Webhook Secret Token, account ${account.index}`} name="webhookSecret" type="password" value={webhookSecret} onChange={setWebhookSecret} saved={account.webhookSecretSet} />
      </div>
      <button className="secondary-button" type="submit" disabled={pending}>{pending ? <LoaderCircle className="spin" size={14} /> : <Save size={14} />}Save account {account.index}</button>
    </form>
  );
}

function NewZoomAccountForm({ index, webhookUrl, pending, onCancel, onSave }: { index: number; webhookUrl: string; pending: boolean; onCancel: () => void; onSave: (input: ZoomAccountInput) => Promise<void> }) {
  const [feedback, setFeedback] = useState<Feedback>(null);
  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const values = new FormData(event.currentTarget);
    const input = {
      index,
      accountId: String(values.get('accountId') || '').trim(),
      clientId: String(values.get('clientId') || '').trim(),
      clientSecret: String(values.get('clientSecret') || ''),
      webhookSecret: String(values.get('webhookSecret') || ''),
    };
    if (!input.accountId || !input.clientId || !input.clientSecret) {
      setFeedback({ tone: 'error', text: 'Account ID, Client ID, and Client Secret are required.' });
      return;
    }
    setFeedback(null);
    try { await onSave(input); } catch { /* Mutation feedback is rendered by the parent panel. */ }
  }
  return (
    <form className="zoom-account-form zoom-account-form--new" onSubmit={event => void submit(event)}>
      <header><strong>Add Zoom account {index}</strong><button className="icon-button" type="button" title="Cancel adding account" aria-label="Cancel adding account" onClick={onCancel}><X size={15} /></button></header>
      <div className="setup-copy-block compact"><span>Use this webhook URL in Zoom</span><div><code>{webhookUrl}</code><CopyButton value={webhookUrl} label="Copy new account webhook URL" /></div></div>
      <div className="setup-form-grid">
        <SetupField label="New account ID" name="accountId" required />
        <SetupField label="New client ID" name="clientId" required />
        <SetupField label="New client secret" name="clientSecret" type="password" required />
        <SetupField label="New webhook secret token" name="webhookSecret" type="password" />
      </div>
      <div className="setup-actions"><button className="secondary-button" type="button" onClick={onCancel}>Cancel</button><button className="primary-button compact" type="submit" disabled={pending}>{pending ? <LoaderCircle className="spin" size={14} /> : <Save size={14} />}Save and connect</button></div>
      {feedback ? <InlineState tone="error"><AlertCircle size={15} />{feedback.text}</InlineState> : null}
    </form>
  );
}

function GuestAccessPanel({ config, loading, error }: { config: GuestAccessConfig | undefined; loading: boolean; error: string | null }) {
  const queryClient = useQueryClient();
  const [passcode, setPasscode] = useState('');
  const [feedback, setFeedback] = useState<Feedback>(null);
  const save = useMutation({
    mutationFn: saveGuestPasscode,
    onSuccess: result => {
      queryClient.setQueryData(queryKeys.guestAccess, result);
      setPasscode('');
      setFeedback({ tone: 'ok', text: result.passcodeSet ? 'Guest passcode saved.' : 'Guest access updated.' });
    },
    onError: mutationError => setFeedback({ tone: 'error', text: errorMessage(mutationError, 'Guest passcode could not be saved.') }),
  });
  const liveLink = config ? `${window.location.origin}${appPath('/guest')}?g=${encodeURIComponent(config.token)}` : '';
  const attendanceLink = liveLink ? `${liveLink}&to=stats` : '';
  function submit(event: FormEvent) {
    event.preventDefault();
    const clean = passcode.trim();
    if (clean.length < 4) {
      setFeedback({ tone: 'error', text: 'Passcode must be at least 4 characters.' });
      return;
    }
    save.mutate(clean);
  }
  return (
    <SetupPanel icon={<ShieldCheck size={16} />} title="Remote guest access">
      <p className="setup-description">Share a read-only chat or attendance view for your events. Both links use the same passcode.</p>
      {loading ? <InlineState><LoaderCircle className="spin" size={15} />Loading guest access</InlineState> : null}
      {error ? <InlineState tone="error"><AlertCircle size={15} />{error}</InlineState> : null}
      {config ? <InlineState tone={config.passcodeSet ? 'ok' : undefined}>{config.passcodeSet ? <CheckCircle2 size={15} /> : <AlertCircle size={15} />}{config.passcodeSet ? 'Passcode set' : 'Set a passcode to enable these links'}</InlineState> : null}
      <form className="setup-inline-form" onSubmit={submit}>
        <SetupField label="Guest passcode" name="guestPasscode" type="password" value={passcode} onChange={setPasscode} placeholder={config?.passcodeSet ? 'Saved, type to change' : 'Minimum 4 characters'} />
        <button className="primary-button compact" type="submit" disabled={save.isPending}>{save.isPending ? <LoaderCircle className="spin" size={14} /> : <Save size={14} />}Save passcode</button>
      </form>
      {liveLink ? <GuestLink label="Live chat link" value={liveLink} /> : null}
      {attendanceLink ? <GuestLink label="Attendance link" value={attendanceLink} /> : null}
      {attendanceLink ? <div className="setup-preview-links"><a href={`${attendanceLink}&view=phone`} target="_blank" rel="noreferrer">Phone layout <ExternalLink size={11} /></a><a href={`${attendanceLink}&view=desktop`} target="_blank" rel="noreferrer">Desktop layout <ExternalLink size={11} /></a></div> : null}
      {feedback ? <InlineState tone={feedback.tone}>{feedback.tone === 'ok' ? <Check size={15} /> : <AlertCircle size={15} />}{feedback.text}</InlineState> : null}
    </SetupPanel>
  );
}

function GuestLink({ label, value }: { label: string; value: string }) {
  return <div className="setup-copy-block compact"><span>{label}</span><div><code>{value}</code><CopyButton value={value} label={`Copy ${label.toLowerCase()}`} /></div></div>;
}

function RecallPanel({ config, loading, error }: { config: RecallConfig | undefined; loading: boolean; error: string | null }) {
  const queryClient = useQueryClient();
  const [apiKey, setApiKey] = useState('');
  const [region, setRegion] = useState(config?.region || 'us-east-1');
  const [feedback, setFeedback] = useState<Feedback>(null);
  const save = useMutation({
    mutationFn: saveRecallConfig,
    onSuccess: async result => {
      setApiKey('');
      setRegion(result.region);
      setFeedback({ tone: 'ok', text: 'Reply bot configuration saved.' });
      await queryClient.invalidateQueries({ queryKey: queryKeys.recallConfig });
    },
    onError: mutationError => setFeedback({ tone: 'error', text: errorMessage(mutationError, 'Reply bot configuration could not be saved.') }),
  });
  function submit(event: FormEvent) {
    event.preventDefault();
    save.mutate({ apiKey, region });
  }
  return (
    <SetupPanel icon={<Bot size={16} />} title="Reply bot">
      {loading ? <InlineState><LoaderCircle className="spin" size={15} />Loading reply bot</InlineState> : null}
      {error ? <InlineState tone="error"><AlertCircle size={15} />{error}</InlineState> : null}
      {config && !config.platform ? (
        <InlineState tone={config.configured ? 'ok' : undefined}>{config.configured ? <CheckCircle2 size={16} /> : <AlertCircle size={16} />}{config.configured ? 'Reply bot ready, managed by your provider' : 'Reply bot is not enabled by your provider yet'}</InlineState>
      ) : null}
      {config?.platform ? (
        <form onSubmit={submit}>
          <p className="setup-description">The platform Recall.ai key is shared with managed accounts. It remains write-only after saving.</p>
          <SetupField label="Recall.ai API key" name="recallApiKey" type="password" value={apiKey} onChange={setApiKey} saved={config.own} placeholder={config.own ? 'Saved, type to change' : 'Recall.ai API key'} />
          <label className="setup-select-field"><span>Region</span><select aria-label="Recall.ai region" value={region} onChange={event => setRegion(event.target.value)}>{recallRegions.map(([value, label]) => <option value={value} key={value}>{value} ({label})</option>)}</select></label>
          <button className="primary-button compact" type="submit" disabled={save.isPending}>{save.isPending ? <LoaderCircle className="spin" size={14} /> : <Save size={14} />}Save bot connection</button>
          {feedback ? <InlineState tone={feedback.tone}>{feedback.tone === 'ok' ? <Check size={15} /> : <AlertCircle size={15} />}{feedback.text}</InlineState> : null}
        </form>
      ) : null}
    </SetupPanel>
  );
}

function WhopPanel({ config, loading, error }: { config: WhopConfig | undefined; loading: boolean; error: string | null }) {
  const queryClient = useQueryClient();
  const [apiKey, setApiKey] = useState('');
  const [companyId, setCompanyId] = useState(config?.companyId || '');
  const [products, setProducts] = useState(config?.products.map(product => `${product.id} | ${product.label}`).join('\n') || '');
  const [feedback, setFeedback] = useState<Feedback>(null);
  const [probe, setProbe] = useState<WhopProbeProduct[] | null>(null);
  const save = useMutation({
    mutationFn: saveWhopConfig,
    onSuccess: async result => {
      setApiKey('');
      setFeedback(result.test.ok
        ? { tone: 'ok', text: 'Whop saved and connection verified.' }
        : { tone: 'error', text: `Saved, but the connection test failed: ${result.test.reason || 'unknown'}` });
      await queryClient.invalidateQueries({ queryKey: queryKeys.whopConfig });
    },
    onError: mutationError => setFeedback({ tone: 'error', text: errorMessage(mutationError, 'Whop configuration could not be saved.') }),
  });
  const probeMutation = useMutation({
    mutationFn: () => probeWhop(),
    onSuccess: setProbe,
    onError: mutationError => setFeedback({ tone: 'error', text: errorMessage(mutationError, 'Whop status breakdown failed.') }),
  });
  function submit(event: FormEvent) {
    event.preventDefault();
    save.mutate({ apiKey, companyId: companyId.trim(), products: parseProducts(products) });
  }
  return (
    <SetupPanel icon={<ShoppingBag size={16} />} title="Whop sales">
      <p className="setup-description">Connect paid products to live sales counts in Stats. The API key remains write-only.</p>
      {loading ? <InlineState><LoaderCircle className="spin" size={15} />Loading Whop setup</InlineState> : null}
      {error ? <InlineState tone="error"><AlertCircle size={15} />{error}</InlineState> : null}
      {config ? (
        <form onSubmit={submit}>
          <InlineState tone={config.keySet ? 'ok' : undefined}>{config.keySet ? <CheckCircle2 size={15} /> : <AlertCircle size={15} />}{config.keySet ? 'API key saved' : 'API key not set'}</InlineState>
          <SetupField label="Whop API key" name="whopApiKey" type="password" value={apiKey} onChange={setApiKey} saved={config.keySet} placeholder={config.keySet ? 'Saved, type to change' : 'apik_...'} />
          <SetupField label="Company ID" name="companyId" value={companyId} onChange={setCompanyId} placeholder="biz_..." />
          <label className="setup-textarea-field"><span>Products <small>one per line: prod_id | Label</small></span><textarea aria-label="Whop products" rows={4} value={products} onChange={event => setProducts(event.target.value)} placeholder="prod_xxxxxxxxxxx | Full Pay" /></label>
          <div className="setup-actions"><button className="primary-button compact" type="submit" disabled={save.isPending}>{save.isPending ? <LoaderCircle className="spin" size={14} /> : <Save size={14} />}Save and test</button><button className="secondary-button" type="button" disabled={probeMutation.isPending || !config.keySet} onClick={() => probeMutation.mutate()}>{probeMutation.isPending ? <LoaderCircle className="spin" size={14} /> : <RefreshCw size={14} />}Status breakdown</button></div>
          {feedback ? <InlineState tone={feedback.tone}>{feedback.tone === 'ok' ? <Check size={15} /> : <AlertCircle size={15} />}{feedback.text}</InlineState> : null}
          {probe ? <ProbeResults products={probe} /> : null}
        </form>
      ) : null}
    </SetupPanel>
  );
}

function ProbeResults({ products }: { products: WhopProbeProduct[] }) {
  return <div className="whop-probe" aria-label="Whop status breakdown">{products.map(product => (
    <div key={product.id}><strong>{product.label || product.id}</strong>{Object.keys(product.byStatus).length ? <dl>{Object.entries(product.byStatus).map(([status, count]) => <div key={status}><dt>{status}</dt><dd>{count}</dd></div>)}</dl> : <span>No memberships</span>}</div>
  ))}</div>;
}

function SetupPanel({ icon, title, action, className = '', children }: { icon: ReactNode; title: string; action?: ReactNode; className?: string; children: ReactNode }) {
  return <section className={`setup-panel ${className}`}><header><div>{icon}<h2>{title}</h2></div>{action}</header><div className="setup-panel__body">{children}</div></section>;
}

interface SetupFieldProps {
  label: string;
  name: string;
  type?: 'text' | 'password';
  value?: string;
  defaultValue?: string;
  placeholder?: string;
  required?: boolean;
  saved?: boolean;
  onChange?: (value: string) => void;
}

function SetupField({ label, name, type = 'text', value, defaultValue, placeholder, required, saved, onChange }: SetupFieldProps) {
  const id = `setup-${label.replace(/[^a-z0-9]+/gi, '-').replace(/^-|-$/g, '').toLowerCase()}`;
  return <label className="setup-field" htmlFor={id}><span>{label}{saved ? <small><Check size={11} />saved</small> : null}</span><input id={id} name={name} type={type} value={value} defaultValue={value === undefined ? defaultValue : undefined} placeholder={placeholder} required={required} autoComplete="off" onChange={onChange ? event => onChange(event.target.value) : undefined} /></label>;
}

function InlineState({ tone, children }: { tone?: 'ok' | 'error' | undefined; children: ReactNode }) {
  return <div className={`setup-inline-state ${tone || ''}`} role={tone === 'error' ? 'alert' : 'status'}>{children}</div>;
}

function CopyButton({ value, label }: { value: string; label: string }) {
  const [copied, setCopied] = useState(false);
  async function copy() {
    await navigator.clipboard.writeText(value);
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1200);
  }
  return <button className="icon-button" type="button" title={copied ? 'Copied' : label} aria-label={label} onClick={() => void copy()}>{copied ? <Check size={14} /> : <Clipboard size={14} />}</button>;
}

function parseProducts(value: string) {
  return value.split('\n').map(line => line.trim()).filter(Boolean).map(line => {
    const [id = '', ...label] = line.split('|');
    return { id: id.trim(), label: label.join('|').trim() };
  }).filter(product => product.id);
}
